import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MaterialModule } from "./shared/material.module";
import { FlexLayoutModule } from "@angular/flex-layout";
import { LayoutComponent } from "./components/layout/layout.component";
import { HeaderComponent } from "./components/navigation/header/header.component";
import { FooterComponent } from "./components/navigation/footer/footer.component";
import { SidenavComponent } from "./components/navigation/sidenav/sidenav.component";
import { WelcomeComponent } from "./components/welcome/welcome.component";
import { DashboardComponent } from "./components/dashboard/dashboard.component";
import { HTTP_INTERCEPTORS, HttpClientModule } from "@angular/common/http";
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
} from "@azure/msal-browser";
import {
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MsalInterceptorConfiguration,
  MsalService,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
  MsalGuardConfiguration,
  MsalRedirectComponent,
} from "@azure/msal-angular";
import { ScormRuntime12Component } from "./components/scorm-runtime12/scorm-runtime12.component";
import { ExternalRuntimeComponent } from "./components/external-runtime/external-runtime.component";
import {
  ErrorStateMatcher,
  ShowOnDirtyErrorStateMatcher,
} from "@angular/material/core";
import { CreateUserprofileComponent } from "./components/create-userprofile/create-userprofile.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  msalConfig,
  loginRequestEnglish,
  loginRequestWelsh,
  loginRequest,
} from "./auth-config";
import { CoursesComponent } from "./views/courses/courses.component";
import { InitialAssessmentComponent } from "./views/initial-assessment/initial-assessment.component";
import { IfRolesDirective } from "./directives/if-roles.directive";
import { UsersComponent } from "./components/users/users.component";
import { ScreenerComponent } from "./views/screener/screener.component";
import { ScormNavCloseComponent } from "./components/scorm-nav-close/scorm-nav-close.component";
import { DateAdapter, MAT_DATE_FORMATS } from "@angular/material/core";
import {
  AppDateAdapter,
  APP_DATE_FORMATS,
} from "src/app/shared/date-and-time-format";
import { LearningComponent } from "./views/learning/learning.component";
import { LoadingPageComponent } from "./views/loading-page/loading-page.component";
import { LearnerCourseComponent } from "./components/learner-course/learner-course.component";
import { NotFoundPageComponent } from "./views/not-found-page/not-found-page.component";
import { DatePipe } from "@angular/common";
import { DirectAccessGuard } from "./guards/direct-access.guard";
import { LearningGuard } from "./guards/learning.guard";
import { UpdateUserprofileComponent } from "./components/update-userprofile/update-userprofile.component";
import { ComponentExitGuard } from "./guards/component-exit.guard";
import { BrowserNoteComponent } from "./components/browser-note/browser-note.component";
import { BaseComponent } from "./components/base/base-component.component";
import { TimeoutWarningDialogComponent } from "./views/timeout-warning-dialog/timeout-warning-dialog.component";
import { NgIdleModule } from "@ng-idle/core";
import { IAppConfig } from "./lib/config/app-config";
import { APP_CONFIG } from "./lib/angular/injection-tokens";
import { AdminComponent } from "./views/admin/admin.component";
import { SideNavComponent } from "./components/admin/side-nav/side-nav.component";
import { HttpConfigInterceptor } from "./_interceptors/http-config.interceptor";
import { DeleteDialogComponent } from "./views/delete-dialog/delete-dialog.component";
import { AppConstants } from "./constants/constants";
import { CoreStoreModule } from "./_store/_store.module";
import { SharedModule } from "./shared/shared.module";
import { LoadingScreenComponent } from "./views/loading-screen/loading-screen.component";
import { ContactUsComponent } from "./components/footer/contact-us/contact-us.component";
import { PdfViewerModule } from "ng2-pdf-viewer";
import { PrivacyPolicyComponent } from "./components/footer/privacy-policy/privacy-policy.component";
import { CopyrightComponent } from "./components/footer/copyright/copyright.component";
import { AccessibilityComponent } from "./components/footer/accessibility/accessibility.component";
import { InformationFaqComponent } from "./components/header/information-faq/information-faq.component";
import { InformationHowDoesItWorkComponent } from "./components/header/information-how-does-it-work/information-how-does-it-work.component";
import { TutorComponent } from "./views/tutor/tutor.component";
import { InformationTutorResourcesComponent } from "./components/header/information-tutor-resources/information-tutor-resources.component";
import { UpdateStatusDialogComponent } from "./views/update-status-dialog/update-status-dialog.component";
import { AssignTargetComponent } from "./views/assign-target/assign-target.component";
import { DeleteLearnerDialogComponent } from "./views/delete-learner-dialog/delete-learner-dialog.component";
import { DeleteGroupDialogComponent } from "./views/delete-group-dialog/delete-group-dialog.component";
import { DeleteCentreDialogComponent } from "./views/delete-centre-dialog/delete-centre-dialog.component";
import { LearnerResourcesComponent } from "./views/learner-resources/learner-resources.component";
import { ConfirmTransferDialogComponent } from "./components/dialog/confirm-transfer-dialog/confirm-transfer-dialog.component";
import { DeleteOrganisationDialogComponent } from "./views/delete-organisation-dialog/delete-organisation-dialog.component";
import { InformationLearnerGuideComponent } from "./components/header/information-learner-guide/information-learner-guide.component";
import { DigitalLitSelfAssessmentComponent } from "./views/digital-lit-self-assessment/digital-lit-self-assessment.component";
import { NgxChartsModule } from "@swimlane/ngx-charts";
import { FsPlayerComponent } from './components/fs-player/fs-player/fs-player.component';
import { MsalsignedinComponent } from "./components/msalsignedin/msalsignedin.component";
/**
 * Here we pass the configuration parameters to create an MSAL instance.
 * For more info, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/configuration.md
 */
export function MSALInstanceFactory(
  config: IAppConfig
): IPublicClientApplication {
  const langId = window.location.href.split(AppConstants.SLASH)[3];

  if (AppConstants.VALID_LANG_CODES.includes(langId.toString())) {
    msalConfig.auth.redirectUri = AppConstants.SLASH.concat(
      langId.toString().concat(AppConstants.MSAL_SIGNED_IN_PAGE)
    );
  }

  let welcomeUrl: string = AppConstants.WELCOME_PAGE;
  if (AppConstants.VALID_LANG_CODES.includes(langId.toString())) {
    welcomeUrl = AppConstants.SLASH.concat(
      langId.toString().concat(AppConstants.WELCOME_PAGE)
    );
  }

  msalConfig.auth.postLogoutRedirectUri = welcomeUrl;
  msalConfig.auth.clientId = config.adB2cClientId;
  msalConfig.auth.knownAuthorities = [config.b2cPolicies.authorityDomain];
  msalConfig.auth.authority = config.b2cPolicies.authorities.signIn;

  return new PublicClientApplication(msalConfig);
}

/**
 * MSAL Angular will automatically retrieve tokens for resources
 * added to protectedResourceMap. For more info, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/v2-docs/initialization.md#get-tokens-for-web-api-calls
 */
export function MSALInterceptorConfigFactory(
  config: IAppConfig
): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();

  protectedResourceMap.set(config.protectedResourceEndpoint, [
    config.protectedResourceDefaultScope,
  ]);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

/**
 * Set your default interaction type for MSALGuard here. If you have any
 * additional scopes you want the user to consent upon login, add them here as well.
 */
export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  let siteLanguageCode: string = window.location.pathname.split(
    AppConstants.SLASH
  )[1];
  if (siteLanguageCode == AppConstants.WELSH_LANG_CODE) {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: loginRequestWelsh,
    };
  } else if (siteLanguageCode == AppConstants.DEFAULT_LANG_CODE) {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: loginRequestEnglish,
    };
  } else {
    return {
      interactionType: InteractionType.Redirect,
      authRequest: loginRequest,
    };
  }
}

@NgModule({
  declarations: [
    AppComponent,
    LayoutComponent,
    HeaderComponent,
    FooterComponent,
    SidenavComponent,
    WelcomeComponent,
    DashboardComponent,
    ScormRuntime12Component,
    CoursesComponent,
    CreateUserprofileComponent,
    InitialAssessmentComponent,
    AssignTargetComponent,
    IfRolesDirective,
    UsersComponent,
    ScreenerComponent,
    ScormNavCloseComponent,
    LearningComponent,
    LoadingPageComponent,
    LearnerCourseComponent,
    NotFoundPageComponent,
    UpdateUserprofileComponent,
    BrowserNoteComponent,
    BaseComponent,
    TimeoutWarningDialogComponent,
    ExternalRuntimeComponent,
    AdminComponent,
    TutorComponent,
    SideNavComponent,
    DeleteDialogComponent,
    LoadingScreenComponent,
    ContactUsComponent,
    PrivacyPolicyComponent,
    CopyrightComponent,
    AccessibilityComponent,
    InformationFaqComponent,
    InformationHowDoesItWorkComponent,
    InformationTutorResourcesComponent,
    UpdateStatusDialogComponent,
    DeleteLearnerDialogComponent,
    DeleteGroupDialogComponent,
    DeleteCentreDialogComponent,
    LearnerResourcesComponent,
    ConfirmTransferDialogComponent,
    DeleteOrganisationDialogComponent,
    InformationLearnerGuideComponent,
    DigitalLitSelfAssessmentComponent,
    FsPlayerComponent,
    MsalsignedinComponent
  ],
  exports: [BrowserNoteComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    MaterialModule,
    FlexLayoutModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    NgIdleModule.forRoot(),
    CoreStoreModule,
    SharedModule,
    PdfViewerModule,
    NgxChartsModule,
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpConfigInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
      deps: [APP_CONFIG],
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
      deps: [APP_CONFIG],
    },
    DatePipe,
    DirectAccessGuard,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    LearningGuard,
    ComponentExitGuard,
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
  ],
  bootstrap: [AppComponent, MsalRedirectComponent],
})
export class AppModule {}
